import { CommonModule } from '@angular/common';
import { Component, DestroyRef, HostListener, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import alertify from 'alertifyjs';
import { ApiServiceService } from '../../modules/services/api-service.service';
import { AuthenticationService } from '../../modules/services/authentication.service';
import { filter } from 'rxjs/operators';
import { WorkflowNotificationService } from '../../modules/services/workflow-notification.service';

@Component({
  selector: 'app-landing-navbar',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './landing-navbar.component.html',
  styleUrl: './landing-navbar.component.scss',
})
export class LandingNavbarComponent {
  mobileMenuOpen = false;
  isScrolled = false;
  isLoggingOut = false;
  private readonly destroyRef = inject(DestroyRef);
  readonly navLinks = [
    { label: 'About', sectionId: 'about' },
    { label: 'How it works', sectionId: 'how-it-works' },
    { label: 'Features', sectionId: 'features' },
    { label: 'FAQ', sectionId: 'faq' },
    { label: 'Contact', sectionId: 'contact' },
  ] as const;

  constructor(
    private readonly router: Router,
    private readonly apiService: ApiServiceService,
    private readonly authenticationService: AuthenticationService,
    private readonly workflowNotificationService: WorkflowNotificationService,
  ) {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.closeMobileMenu();
        this.isScrolled = window.pageYOffset > 50;
      });
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.isScrolled = window.pageYOffset > 50;
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
  }

  navigateToSection(sectionId: string, event?: Event): void {
    event?.preventDefault();
    const target = document.getElementById(sectionId);
    if (!target) {
      void this.router.navigate(['/'], { fragment: sectionId });
      return;
    }

    const top = target.getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({ top, behavior: 'smooth' });
    this.closeMobileMenu();
  }

  get isLoginPage(): boolean {
    return this.router.url.startsWith('/login');
  }

  get isRegisterPage(): boolean {
    return this.router.url.startsWith('/register');
  }

  get isAuthenticated(): boolean {
    return this.authenticationService.isAuthenticated();
  }

  logout(): void {
    if (this.isLoggingOut) return;

    this.isLoggingOut = true;

    this.apiService.logout().subscribe({
      next: () => {
        this.workflowNotificationService.disconnect();
        this.authenticationService.clearAuthenticatedSession();
        this.closeMobileMenu();
        alertify.success('You have been logged out.');
        this.isLoggingOut = false;
        void this.router.navigate(['/login']);
      },
      error: (error) => {
        this.workflowNotificationService.disconnect();
        this.authenticationService.clearAuthenticatedSession();
        this.closeMobileMenu();
        alertify.error(
          typeof error === 'string'
            ? error
            : 'We could not complete logout cleanly, but your local session has been cleared.',
        );
        this.isLoggingOut = false;
        void this.router.navigate(['/login']);
      },
    });
  }

  goToDashboard() {
    const accountType = this.authenticationService.getAccountType();

    if (accountType === "ADMIN") {
      this.router.navigate(["admin"]);
    } else if(accountType === "USER") {
      this.router.navigate(["user"]);
    }
  }
}
